/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  setDoc,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { ref as storageRef, uploadBytes } from "firebase/storage";
import { useNavigate, useOutletContext } from "react-router-dom";
import { auth, db, storage } from "../firebase";
import "./ParentMobile.css";

function todayKey() {
  return getLocalDateKey();
}

const SAME_DAY_ABSENCE_CUTOFF = "10:00";
const MAX_FUTURE_ABSENCE_DAYS = 30;

function getLocalDateKey(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getMinutesFromTime(value) {
  const [hours, minutes] = String(value || "").split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return 600;
  return hours * 60 + minutes;
}

function getCurrentMinutes() {
  const now = new Date();
  return now.getHours() * 60 + now.getMinutes();
}

function addDaysToDateKey(dateKey, days) {
  const date = parseDateKey(dateKey);
  if (!date) return null;
  date.setUTCDate(date.getUTCDate() + Number(days || 0));
  return date.toISOString().slice(0, 10);
}

function cleanEvidenceFileName(value) {
  return String(value || "evidence")
    .trim()
    .replace(/[^\w.\-() ]+/g, "_")
    .replace(/\s+/g, "_")
    .slice(0, 120);
}

function ReasonIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3a9 9 0 1 0 9 9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
      <path d="m9 12 2 2 4-5" />
    </svg>
  );
}

function ProofIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
      <path d="M14 2v6h6" />
      <path d="M9 15h6" />
      <path d="M9 18h3" />
    </svg>
  );
}

function isAllowedProofFile(file) {
  if (!file) return true;

  const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];
  const maxBytes = 3 * 1024 * 1024;

  return allowedTypes.includes(file.type) && file.size <= maxBytes;
}

function addDaysTimestamp(days) {
  const value = Number(days || 7);
  return Timestamp.fromDate(new Date(Date.now() + value * 24 * 60 * 60 * 1000));
}

function parseDateKey(value) {
  const key = String(value || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) return null;
  const date = new Date(`${key}T00:00:00.000Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function normalizeHolidayDateKey(value) {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);
  if (typeof value?.toDate === "function") return getLocalDateKey(value.toDate());

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return getLocalDateKey(parsed);
}

function isWeekendDateKey(dateKey) {
  const date = parseDateKey(dateKey);
  if (!date) return false;

  const day = date.getUTCDay();
  return day === 0 || day === 6;
}

function eachDateKeyInRange(startDate, endDate) {
  const start = parseDateKey(startDate);
  const end = parseDateKey(endDate);

  if (!start || !end) return [];

  const rows = [];
  const cursor = new Date(start);

  while (cursor.getTime() <= end.getTime()) {
    rows.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return rows;
}

function holidayDateRange(holiday = {}) {
  const start =
    normalizeHolidayDateKey(
      holiday.startDate ||
        holiday.dateKey ||
        holiday.date ||
        holiday.from ||
        holiday.start
    ) || "";

  const end =
    normalizeHolidayDateKey(
      holiday.endDate ||
        holiday.to ||
        holiday.end ||
        holiday.startDate ||
        holiday.dateKey ||
        holiday.date
    ) || start;

  return { start, end };
}

function getHolidayName(holiday = {}) {
  return (
    holiday.title ||
    holiday.name ||
    holiday.label ||
    holiday.reason ||
    holiday.type ||
    "school holiday"
  );
}

function findHolidayForDate(dateKey, schoolHolidays = [], bankHolidays = []) {
  const bank = bankHolidays.find((item) => String(item.date || "") === dateKey);

  if (bank) {
    return {
      type: "bank",
      title: bank.title || "Bank holiday",
    };
  }

  const manual = schoolHolidays.find((holiday) => {
    const range = holidayDateRange(holiday);
    return range.start && range.end && dateKey >= range.start && dateKey <= range.end;
  });

  if (manual) {
    return {
      type: "school",
      title: getHolidayName(manual),
    };
  }

  return null;
}

function formatDateLabel(value) {
  const date = parseDateKey(value);
  if (!date) return String(value || "");

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function getDurationDays(startDate, endDate) {
  const start = parseDateKey(startDate);
  const end = parseDateKey(endDate);

  if (!start || !end) return 1;

  return Math.max(
    1,
    Math.floor((end.getTime() - start.getTime()) / 86400000) + 1
  );
}

function buildDateRangeLabel(startDate, endDate) {
  const days = getDurationDays(startDate, endDate);

  if (days <= 1) return formatDateLabel(startDate);

  return `${formatDateLabel(startDate)} - ${formatDateLabel(endDate)}`;
}

const styles = {
  page: {
    display: "grid",
    gap: 16,
    paddingBottom: 132,
  },
  hero: {
    position: "relative",
    overflow: "hidden",
    padding: 22,
    borderRadius: 28,
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
    border: "1px solid rgba(224, 232, 247, 0.96)",
    boxShadow: "0 16px 34px rgba(20, 54, 110, 0.08), 0 5px 12px rgba(20, 54, 110, 0.04)",
  },
  glow: {
    position: "absolute",
    width: 180,
    height: 180,
    right: -70,
    top: -90,
    borderRadius: 999,
    background: "radial-gradient(circle, rgba(45, 109, 242, 0.18), transparent 68%)",
    pointerEvents: "none",
  },
  eyebrow: {
    margin: "0 0 8px",
    color: "#7288ad",
    fontSize: 11,
    fontWeight: 950,
    letterSpacing: "0.15em",
    textTransform: "uppercase",
  },
  title: {
    margin: 0,
    color: "#183566",
    fontSize: 26,
    lineHeight: 1.05,
    fontWeight: 950,
    letterSpacing: "-0.04em",
  },
  text: {
    margin: "10px 0 0",
    color: "#6f84a7",
    fontSize: 15,
    lineHeight: 1.55,
  },
  formCard: {
    padding: 20,
    borderRadius: 28,
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
    border: "1px solid rgba(224, 232, 247, 0.96)",
    boxShadow: "0 16px 34px rgba(20, 54, 110, 0.08), 0 5px 12px rgba(20, 54, 110, 0.04)",
  },
  guide: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: 14,
    marginBottom: 18,
    borderRadius: 20,
    background: "linear-gradient(180deg, #edf4ff 0%, #f7fbff 100%)",
    border: "1px solid rgba(224, 232, 247, 0.96)",
  },
  guideIcon: {
    width: 46,
    height: 46,
    flex: "0 0 auto",
    borderRadius: 16,
    display: "grid",
    placeItems: "center",
    color: "#285fd7",
    background: "#ffffff",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7)",
  },
  guideTitle: {
    margin: 0,
    color: "#183566",
    fontSize: 14,
    lineHeight: 1.2,
    fontWeight: 950,
  },
  guideText: {
    margin: "4px 0 0",
    color: "#7288ad",
    fontSize: 13,
    lineHeight: 1.35,
    fontWeight: 700,
  },
  form: {
    display: "grid",
    gap: 14,
  },
  field: {
    display: "grid",
    gap: 7,
    minWidth: 0,
  },
  label: {
    color: "#7288ad",
    fontSize: 11,
    fontWeight: 950,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
  },
  control: {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    minHeight: 50,
    border: "1px solid rgba(224, 232, 247, 0.96)",
    borderRadius: 17,
    background: "#ffffff",
    color: "#183566",
    padding: "0 14px",
    fontSize: 14,
    fontWeight: 800,
    boxSizing: "border-box",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7)",
    WebkitAppearance: "none",
    appearance: "none",
  },
  textarea: {
    width: "100%",
    minHeight: 104,
    border: "1px solid rgba(224, 232, 247, 0.96)",
    borderRadius: 17,
    background: "#ffffff",
    color: "#183566",
    padding: "13px 14px",
    fontSize: 14,
    fontWeight: 750,
    lineHeight: 1.5,
    resize: "vertical",
    boxSizing: "border-box",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7)",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
    minWidth: 0,
    width: "100%",
  },
  proofBox: {
    display: "grid",
    gap: 10,
    padding: 14,
    borderRadius: 20,
    background: "linear-gradient(180deg, #f8fbff 0%, #ffffff 100%)",
    border: "1px solid rgba(224, 232, 247, 0.96)",
  },
  proofOffBox: {
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
    padding: 14,
    borderRadius: 20,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    color: "#64748b",
  },
  proofTop: {
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
  },
  proofIcon: {
    width: 42,
    height: 42,
    flex: "0 0 auto",
    borderRadius: 15,
    display: "grid",
    placeItems: "center",
    color: "#2d61d2",
    background: "#edf4ff",
  },
  proofTitle: {
    margin: 0,
    color: "#183566",
    fontSize: 13,
    fontWeight: 950,
  },
  proofText: {
    margin: "4px 0 0",
    color: "#7288ad",
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.4,
  },
  fileInput: {
    width: "100%",
    padding: "12px",
    borderRadius: 16,
    border: "1px dashed rgba(45, 109, 242, 0.38)",
    background: "#ffffff",
    color: "#183566",
    boxSizing: "border-box",
    fontSize: 13,
    fontWeight: 800,
  },
  fileMeta: {
    padding: "10px 12px",
    borderRadius: 15,
    background: "#ecfdf5",
    color: "#047857",
    fontSize: 12,
    fontWeight: 850,
    lineHeight: 1.4,
  },
  actions: {
    display: "grid",
    gridTemplateColumns: "1.35fr 0.85fr",
    gap: 10,
    marginTop: 4,
  },
  primary: {
    minHeight: 50,
    border: "none",
    borderRadius: 17,
    background: "linear-gradient(180deg, #2d6df2 0%, #4a88ff 100%)",
    color: "#ffffff",
    fontSize: 14,
    fontWeight: 950,
    boxShadow: "0 13px 24px rgba(43, 112, 241, 0.20)",
    cursor: "pointer",
  },
  secondary: {
    minHeight: 50,
    border: "none",
    borderRadius: 17,
    background: "#edf4ff",
    color: "#2d61d2",
    fontSize: 14,
    fontWeight: 950,
    cursor: "pointer",
  },
  success: {
    padding: "13px 14px",
    borderRadius: 17,
    background: "#ecfdf5",
    color: "#047857",
    border: "1px solid rgba(16, 185, 129, 0.18)",
    fontSize: 13,
    fontWeight: 850,
    lineHeight: 1.4,
  },
  error: {
    padding: "13px 14px",
    borderRadius: 17,
    background: "#fff1f2",
    color: "#be123c",
    border: "1px solid rgba(244, 63, 94, 0.18)",
    fontSize: 13,
    fontWeight: 850,
    lineHeight: 1.4,
  },
};

export default function ParentReportAbsence() {
  const navigate = useNavigate();
const {
  profile,
  activeChild,
  activeChildId,
  isOnline = typeof navigator === "undefined" ? true : navigator.onLine,
  offlineMessage = "You are offline. Please reconnect and try again.",
} = useOutletContext() || {};

  const schoolId = profile?.schoolId || activeChild?.schoolId || "";
  const schoolName = profile?.schoolName || "Your School";
  const parentId = auth.currentUser?.uid || profile?.uid || profile?.id || "";
  const parentName = profile?.name || profile?.displayName || "Parent";
  const parentEmail = auth.currentUser?.email || profile?.email || "";
  const studentId = activeChildId || activeChild?.id || activeChild?.studentId || activeChild?.docId || "";
  const childName = activeChild?.name || activeChild?.fullName || "Child";

  const [form, setForm] = useState({
    reason: "",
    dateKey: todayKey(),
    startDate: todayKey(),
    endDate: todayKey(),
    note: "",
    duration: "full_day",
  });

  const [absenceSettings, setAbsenceSettings] = useState({
    allowEvidenceUploads: false,
    evidenceRetentionHoursAfterDecision: 72,
    evidenceHardDeleteDays: 7,
    sameDayCutoffTime: SAME_DAY_ABSENCE_CUTOFF,
    maxFutureAbsenceDays: MAX_FUTURE_ABSENCE_DAYS,
  });

  const [schoolHolidays, setSchoolHolidays] = useState([]);
  const [bankHolidays, setBankHolidays] = useState([]);
  const [proofFile, setProofFile] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const offlineBlocked = isOnline === false;

  const allowEvidenceUploads = !!absenceSettings.allowEvidenceUploads;
  const sameDayCutoffTime = absenceSettings.sameDayCutoffTime || SAME_DAY_ABSENCE_CUTOFF;
  const maxFutureAbsenceDays = Number(absenceSettings.maxFutureAbsenceDays || MAX_FUTURE_ABSENCE_DAYS);

  function validateCurrentAbsenceWindow(startDate, endDate) {
    const today = getLocalDateKey();
    const cutoffMinutes = getMinutesFromTime(sameDayCutoffTime);
    const nowMinutes = getCurrentMinutes();
    const maxFutureDate = addDaysToDateKey(today, maxFutureAbsenceDays);

    if (!startDate || !endDate) {
      return "Please choose a start date and end date.";
    }

    if (startDate < today || endDate < today) {
      return "Past absence dates cannot be reported through the parent app. Please contact the school office directly.";
    }

    if (startDate === today && nowMinutes >= cutoffMinutes) {
      return "Same-day absence reporting has closed for today. Please contact the school office directly for any urgent absence update.";
    }

    if (maxFutureDate && (startDate > maxFutureDate || endDate > maxFutureDate)) {
      return `Absence reports can only be submitted up to ${maxFutureAbsenceDays} days in advance.`;
    }

    const selectedDates = eachDateKeyInRange(startDate, endDate);

    const weekendDate = selectedDates.find((dateKey) => isWeekendDateKey(dateKey));
    if (weekendDate) {
      return "Please choose a school day. Weekends cannot be selected for absence reports.";
    }

    const holidayDate = selectedDates.find((dateKey) =>
      findHolidayForDate(dateKey, schoolHolidays, bankHolidays)
    );

    if (holidayDate) {
      const holiday = findHolidayForDate(holidayDate, schoolHolidays, bankHolidays);
      return `${formatDateLabel(holidayDate)} is ${holiday?.title || "a school holiday"}. Please choose a normal school day.`;
    }

    return "";
  }

  useEffect(() => {
    let alive = true;

    async function loadAbsenceSettings() {
      if (!schoolId) {
        setLoadingSettings(false);
        return;
      }

      try {
        setLoadingSettings(true);
        const snap = await getDoc(doc(db, "schools", schoolId, "settings", "absence"));
        const data = snap.exists() ? snap.data() || {} : {};

        if (!alive) return;

        setAbsenceSettings({
          allowEvidenceUploads: !!data.allowEvidenceUploads,
          evidenceRetentionHoursAfterDecision: Number(data.evidenceRetentionHoursAfterDecision || 72),
          evidenceHardDeleteDays: Number(data.evidenceHardDeleteDays || 7),
          sameDayCutoffTime: String(data.sameDayCutoffTime || data.absenceCutoffTime || SAME_DAY_ABSENCE_CUTOFF),
          maxFutureAbsenceDays: Number(data.maxFutureAbsenceDays || MAX_FUTURE_ABSENCE_DAYS),
        });
      } catch (err) {
        console.warn("Failed to load absence evidence settings:", err);
      } finally {
        if (alive) setLoadingSettings(false);
      }
    }

    loadAbsenceSettings();

    return () => {
      alive = false;
    };
  }, [schoolId]);

  useEffect(() => {
    if (!schoolId) {
      setSchoolHolidays([]);
      return undefined;
    }

    const unsub = onSnapshot(
      collection(db, "schools", schoolId, "schoolHolidays"),
      (snap) => {
        const rows = [];
        snap.forEach((d) => rows.push({ id: d.id, ...(d.data() || {}) }));
        setSchoolHolidays(rows);
      },
      (err) => {
        console.warn("Failed to load school holidays for absence form:", err);
        setSchoolHolidays([]);
      }
    );

    return () => unsub();
  }, [schoolId]);

  useEffect(() => {
    let alive = true;

    async function loadBankHolidays() {
      try {
        const res = await fetch("https://www.gov.uk/bank-holidays.json");
        const data = await res.json();
        const events = data?.["england-and-wales"]?.events || [];

        if (!alive) return;

        setBankHolidays(
          events.map((item) => ({
            date: item.date,
            title: item.title,
          }))
        );
      } catch (err) {
        console.warn("Bank holidays failed to load for absence form:", err);
        if (alive) setBankHolidays([]);
      }
    }

    loadBankHolidays();

    return () => {
      alive = false;
    };
  }, []);

  const canSubmit = useMemo(() => {
    return Boolean(schoolId && studentId && form.reason && form.dateKey && !loading);
  }, [schoolId, studentId, form.reason, form.dateKey, loading]);

  function onChange(e) {
    const { name, value } = e.target;

    setError("");

    if (name === "startDate" || name === "endDate") {
      if (value) {
        if (isWeekendDateKey(value)) {
          setError("Please choose a school day. Weekends cannot be selected for absence reports.");
          return;
        }

        const holiday = findHolidayForDate(value, schoolHolidays, bankHolidays);
        if (holiday) {
          setError(`${formatDateLabel(value)} is ${holiday.title}. Please choose a normal school day.`);
          return;
        }
      }
    }

    setForm((current) => {
      const next = { ...current, [name]: value };

      if (name === "startDate") {
        next.dateKey = value;
        if (
          !next.endDate ||
          parseDateKey(next.endDate)?.getTime() < parseDateKey(value)?.getTime()
        ) {
          next.endDate = value;
        }
      }

      if (
        name === "endDate" &&
        parseDateKey(value)?.getTime() < parseDateKey(next.startDate)?.getTime()
      ) {
        next.startDate = value;
        next.dateKey = value;
      }

      return next;
    });
  }

  function onFileChange(e) {
    const file = e.target.files?.[0] || null;

    if (!allowEvidenceUploads) {
      setProofFile(null);
      setError("Your school has not enabled supporting document uploads.");
      e.target.value = "";
      return;
    }

    if (file && !isAllowedProofFile(file)) {
      setProofFile(null);
      setError("Only PDF, JPG or PNG files under 3MB are allowed.");
      e.target.value = "";
      return;
    }

    setError("");
    setProofFile(file);
  }

  async function uploadProofWithSignedUrl(reportId) {
  if (!proofFile || !allowEvidenceUploads) return null;

  const currentUser = auth.currentUser;

  if (!currentUser) {
    throw new Error("Please sign in again before uploading evidence.");
  }

  const safeName = cleanEvidenceFileName(proofFile.name);
  const filePath = `absenceProofs/${schoolId}/${reportId}/${Date.now()}_${safeName}`;

  const fileRef = storageRef(storage, filePath);

  await uploadBytes(fileRef, proofFile, {
    contentType: proofFile.type,
    customMetadata: {
      schoolId,
      reportId,
      uploadedBy: currentUser.uid,
      source: "parent_app",
    },
  });

  return {
    proofPath: filePath,
    proofName: proofFile.name,
    proofType: proofFile.type,
    proofSize: proofFile.size,
  };
}

async function onSubmit(e) {
  e.preventDefault();
  setError("");
  setSuccess("");

  if (offlineBlocked) {
    setError(offlineMessage);
    setLoading(false);
    return;
  }

    if (!schoolId) {
      setError("No school is linked to this parent account.");
      return;
    }

    if (!studentId) {
      setError("Please select a child first.");
      return;
    }

    if (!form.reason || !form.startDate || !form.endDate) {
      setError("Please choose a reason, start date and end date.");
      return;
    }

    if (parseDateKey(form.endDate)?.getTime() < parseDateKey(form.startDate)?.getTime()) {
      setError("End date cannot be before start date.");
      return;
    }

    const dateWindowError = validateCurrentAbsenceWindow(form.startDate, form.endDate);
    if (dateWindowError) {
      setError(dateWindowError);
      return;
    }

    if (proofFile && !allowEvidenceUploads) {
      setError("Your school has not enabled supporting document uploads.");
      return;
    }

    if (proofFile && !isAllowedProofFile(proofFile)) {
      setError("Only PDF, JPG or PNG files under 3MB are allowed.");
      return;
    }

    try {
      setLoading(true);

      const reportRef = doc(collection(db, "schools", schoolId, "absenceReports"));
      const proofMeta = proofFile ? await uploadProofWithSignedUrl(reportRef.id) : null;

      await setDoc(reportRef, {
        schoolId,
        schoolName,
        studentId,
        studentName: childName,
        parentId,
        parentName,
        parentEmail,
        dateKey: form.startDate,
        date: form.startDate,
        startDate: form.startDate,
        endDate: form.endDate,
        dateRangeLabel: buildDateRangeLabel(form.startDate, form.endDate),
        durationDays: getDurationDays(form.startDate, form.endDate),
        isMultiDay: getDurationDays(form.startDate, form.endDate) > 1,
        reason: form.reason,
        note: form.note.trim(),
        notes: form.note.trim(),
        duration: form.duration,
        status: "submitted",
        source: "parent_app",
        evidenceUploadEnabledAtSubmit: allowEvidenceUploads,
        proofPath: proofMeta?.proofPath || null,
        proofName: proofMeta?.proofName || null,
        proofType: proofMeta?.proofType || null,
        proofSize: proofMeta?.proofSize || null,
        proofUploadedAt: proofMeta ? serverTimestamp() : null,
        proofDeleteAfter: proofMeta ? addDaysTimestamp(absenceSettings.evidenceHardDeleteDays || 7) : null,
        proofDeletedAt: null,
        proofDeletedAfterDecision: false,
        proofDeleteStatus: proofMeta ? "pending" : null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        reviewedAt: null,
        reviewedBy: null,
      });

      setSuccess(`Absence report submitted for ${childName}.`);
      setForm({
        reason: "",
        dateKey: todayKey(),
        startDate: todayKey(),
        endDate: todayKey(),
        note: "",
        duration: "full_day",
      });
      setProofFile(null);
    } catch (err) {
      console.error("Failed to submit absence report:", err);
      setError(err?.message || "Could not submit the absence report. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="pp-parent-main-content" style={styles.page}>
      <section style={styles.hero}>
        <div style={styles.glow} />
        <p style={styles.eyebrow}>Absence request</p>
        <h2 style={styles.title}>Report an absence</h2>
        <p style={styles.text}>
          Let {schoolName} know quickly when {childName} will miss registration or the full day.
        </p>
      </section>

      <section style={styles.formCard}>
        <div style={styles.guide}>
          <div style={styles.guideIcon}>
            <ShieldIcon />
          </div>
          <div>
            <p style={styles.guideTitle}>Secure school notice</p>
            <p style={styles.guideText}>
              This goes straight to the school office for review. Same-day absence reports close at 10:00 AM; after that, please contact the school directly.
            </p>
          </div>
        </div>

        <form style={styles.form} onSubmit={onSubmit}>
          <div style={styles.field}>
            <label style={styles.label} htmlFor="absence-child">Child</label>
            <input id="absence-child" style={styles.control} value={childName} readOnly />
          </div>

          <div style={styles.field}>
            <label style={styles.label} htmlFor="absence-reason">Reason</label>
            <select
              id="absence-reason"
              name="reason"
              value={form.reason}
              onChange={onChange}
              style={styles.control}
            >
              <option value="">Select a reason</option>
              <option value="illness">Illness</option>
              <option value="medical_appointment">Medical appointment</option>
              <option value="dental_appointment">Dental appointment</option>
              <option value="hospital_appointment">Hospital appointment</option>
              <option value="family_emergency">Family emergency</option>
              <option value="authorised_leave">Authorised leave</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="pp-absence-date-row" style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label} htmlFor="absence-start-date">Start date</label>
              <input
                id="absence-start-date"
                type="date"
                name="startDate"
                className="pp-absence-date-input"
                value={form.startDate}
                min={getLocalDateKey()}
                max={addDaysToDateKey(getLocalDateKey(), maxFutureAbsenceDays) || undefined}
                onChange={onChange}
                style={styles.control}
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label} htmlFor="absence-end-date">End date</label>
              <input
                id="absence-end-date"
                type="date"
                name="endDate"
                className="pp-absence-date-input"
                value={form.endDate}
                min={form.startDate || getLocalDateKey()}
                max={addDaysToDateKey(getLocalDateKey(), maxFutureAbsenceDays) || undefined}
                onChange={onChange}
                style={styles.control}
              />
            </div>
          </div>

          <div style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label} htmlFor="absence-duration">Duration</label>
              <select
                id="absence-duration"
                name="duration"
                value={form.duration}
                onChange={onChange}
                style={styles.control}
              >
                <option value="full_day">Full day</option>
                <option value="morning_only">Morning only</option>
                <option value="afternoon_only">Afternoon only</option>
                <option value="late_arrival">Late arrival</option>
              </select>
            </div>
          </div>

          <div style={styles.field}>
            <label style={styles.label} htmlFor="absence-note">Extra notes</label>
            <textarea
              id="absence-note"
              name="note"
              value={form.note}
              onChange={onChange}
              style={styles.textarea}
              placeholder="Add any extra information for the school office"
            />
          </div>

          {loadingSettings ? null : allowEvidenceUploads ? (
            <div style={styles.proofBox}>
              <div style={styles.proofTop}>
                <div style={styles.proofIcon}>
                  <ProofIcon />
                </div>
                <div>
                  <p style={styles.proofTitle}>Optional supporting document (e.g. appointment letter)</p>
                  <p style={styles.proofText}>
                    PDF, JPG or PNG only. Maximum 3MB. Please do not upload medical diagnoses or sensitive personal details. This document will be temporarily reviewed by the school and automatically deleted.
                  </p>
                </div>
              </div>

              <input
                type="file"
                accept="application/pdf,image/jpeg,image/png"
                onChange={onFileChange}
                style={styles.fileInput}
                disabled={loading}
              />

              {proofFile ? (
                <div style={styles.fileMeta}>
                  Selected: {proofFile.name} · {(proofFile.size / 1024 / 1024).toFixed(2)}MB
                </div>
              ) : null}
            </div>
          ) : (
            <div style={styles.proofOffBox}>
              <div style={styles.proofIcon}>
                <ProofIcon />
              </div>
              <div>
                <p style={styles.proofTitle}>Supporting document upload is not enabled</p>
                <p style={styles.proofText}>
                  Your school currently accepts absence reports using reason and notes only.
                </p>
              </div>
            </div>
          )}

          {success ? <div style={styles.success}>{success}</div> : null}
          {error ? <div style={styles.error}>{error}</div> : null}

          <div style={styles.actions}>
<button
  type="submit"
  style={{
    ...styles.primary,
    opacity: !canSubmit || loading || offlineBlocked ? 0.62 : 1,
    cursor: !canSubmit || loading || offlineBlocked ? "not-allowed" : "pointer",
  }}
  disabled={!canSubmit || loading || offlineBlocked}
>
  {offlineBlocked ? "Offline" : loading ? "Submitting..." : "Submit absence"}
</button>

            <button
              type="button"
              style={styles.secondary}
              onClick={() => navigate("/parent/home")}
              disabled={loading}
            >
              Cancel
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
